
'use client'
    
import { collection, getDocs, query, where, limit, addDoc, serverTimestamp, documentId, orderBy, doc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { Artist, ArtItem, WpArticle } from '@/types';
import { HomeHeader } from '@/components/home/home-header';
import Image from 'next/image';
import Link from 'next/link';
import { notFound, useParams } from 'next/navigation';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ArrowRight, Bookmark, CalendarDays, Edit, ExternalLink, Globe2, ImageIcon, Layers, Loader2, MessageSquare, PlusCircle, ShieldCheck, TrendingUp, Users } from 'lucide-react';
import { useEffect, useState, useMemo } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { Footer } from '@/components/footer';
import { cn, getArtworkImageUrl, shouldBypassNextImageOptimization, toCloudflareImage, toWordpressCloudflareImage } from '@/lib/utils';
import { useAuth } from '@/components/providers/auth-provider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';

const InstagramIcon = (props: React.SVGProps<SVGSVGElement>) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
        <rect width="20" height="20" x="2" y="2" rx="5" ry="5"></rect>
        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
        <line x1="17.5" x2="17.51" y1="6.5" y2="6.5"></line>
    </svg>
);

const TwitterIcon = (props: React.SVGProps<SVGSVGElement>) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
        <path d="M22 4s-.7 2.1-2 3.4c1.6 1.4 3.3 4.4 3.3 4.4s-1.4 1.4-2.8 1.4c-.7 0-1.4-.4-1.4-.4s-2.8 2.8-7 2.8c-3.5 0-7-2.8-7-2.8s-1.4 1.4-2.8 1.4c-1.4 0-2.8-1.4-2.8-1.4s1.7-3 3.3-4.4c-1.3-1.3-2-3.4-2-3.4s1.4-1.4 2.8-1.4c.7 0 1.4.4 1.4.4s2.8-2.8 7-2.8c3.5 0 7 2.8 7 2.8z"></path>
    </svg>
);

const BlueskyIcon = (props: React.SVGProps<SVGSVGElement>) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24_24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
        <path d="M13.5 6.5C13.5 6.5 15 8 16.5 8C18 8 19.5 6.5 19.5 6.5C19.5 6.5 18 5 16.5 5C15 5 13.5 6.5 13.5 6.5z"></path>
        <path d="M10.5 10.5C10.5 10.5 12 12 13.5 12C15 12 16.5 10.5 16.5 10.5C16.5 10.5 15 9 13.5 9C12 9 10.5 10.5 10.5 10.5z"></path>
        <path d="M7.5 14.5C7.5 14.5 9 16 10.5 16C12 16 13.5 14.5 13.5 14.5C13.5 14.5 12 13 10.5 13C9 13 7.5 14.5 7.5 14.5z"></path>
        <path d="M4.5 18.5C4.5 18.5 6 20 7.5 20C9 20 10.5 18.5 10.5 18.5C10.5 18.5 9 17 7.5 17C6 17 4.5 18.5 4.5 18.5z"></path>
    </svg>
);


async function getArtworksByArtist(artistName: string): Promise<ArtItem[]> {
    if (!artistName) return [];
    
    const artworksCol = collection(db, 'artworks');
    const q = query(
        artworksCol, 
        where('artistNames', 'array-contains', artistName), 
        where('visibility', '==', 'public'),
        orderBy('createdAt', 'desc')
    );
    const artworksSnapshot = await getDocs(q);

    const artworksList = artworksSnapshot.docs.map(doc => {
         const data = doc.data();

        let price = 0;
        if (typeof data.price === 'number') {
            price = data.price;
        } else if (typeof data.price === 'string') {
            price = Math.round(parseFloat(data.price.replace(/[^0-9.-]+/g, "")) * 100);
        }
        
        const imageUrls = data.imageUrls || [data.imageUrl || `https://placehold.co/600x800.png`];
        
        const rawYear = data.year;
        const year =
          typeof rawYear === 'number'
            ? rawYear
            : typeof rawYear === 'string'
              ? parseInt(rawYear as unknown as string, 10)
              : undefined;

         return {
            id: doc.id,
            title: data.title,
            artistNames: data.artistNames || [],
            year: Number.isFinite(year) ? year : undefined,
            description: data.description,
            price: price,
            currency: data.currency,
            imageUrl: data.imageUrl,
            imageUrls: imageUrls,
            imageAssets: data.imageAssets,
            imageMetadata: data.imageMetadata,
            searchTerms: data.searchTerms || [],
            createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
            status: data.status,
            visibility: 'public'
         } as ArtItem
    });

    return artworksList;
}

interface ArtistDetailProps {
    initialArtist: Artist;
    initialRelatedArticles: WpArticle[];
    initialFrequentCollaborators: Artist[];
}

function RelatedArticleCard({ article }: { article: WpArticle }) {
    const featuredMedia = article._embedded?.['wp:featuredmedia']?.[0];
    const imageUrl = featuredMedia ? toWordpressCloudflareImage(featuredMedia.source_url) : 'https://placehold.co/600x400.png';

    return (
        <Link href={`/blog/${article.slug}`} className="group">
            <article className="grid grid-cols-[96px_1fr] gap-4 rounded-md border bg-background p-3 transition-all duration-300 group-hover:-translate-y-1 group-hover:shadow-lg motion-reduce:transition-none">
                <div className="relative h-28 overflow-hidden rounded-sm bg-muted">
                    <Image
                        src={imageUrl}
                        alt={article.title.rendered}
                        fill
                        sizes="96px"
                        className="object-cover"
                        unoptimized={shouldBypassNextImageOptimization(imageUrl)}
                    />
                </div>
                <div className="min-w-0 py-1">
                    <h3
                        className="line-clamp-2 font-headline text-lg leading-tight"
                        dangerouslySetInnerHTML={{ __html: article.title.rendered }}
                    />
                    <div
                        className="mt-2 line-clamp-2 text-sm leading-5 text-muted-foreground"
                        dangerouslySetInnerHTML={{ __html: article.excerpt.rendered }}
                    />
                </div>
            </article>
        </Link>
    )
}

function ArtworkTile({ item, priority }: { item: ArtItem; priority?: boolean }) {
    const imageUrl = getArtworkImageUrl(item, 'md');
    const subtitle = [
        item.year,
        item.searchTerms?.[0] || 'Artwork',
        item.editionSize ? `Ed. of ${item.editionSize}` : null,
    ].filter(Boolean).join('  •  ');

    return (
        <Link href={`/artworks/${item.id}`} className="group min-w-0">
            <article className="h-full rounded-md border bg-background p-3 transition-all duration-300 group-hover:-translate-y-1 group-hover:shadow-lg motion-reduce:transition-none">
                <div className="relative aspect-[4/5] overflow-hidden rounded-sm bg-muted">
                    <Image
                        src={imageUrl}
                        alt={item.title}
                        fill
                        priority={priority}
                        sizes="(max-width: 768px) 50vw, 220px"
                        className="object-cover"
                        unoptimized={shouldBypassNextImageOptimization(imageUrl)}
                    />
                    <Button size="icon" variant="secondary" className="absolute right-2 top-2 h-8 w-8 rounded-sm bg-background/90">
                        <Bookmark className="h-4 w-4" />
                    </Button>
                </div>
                <div className="pt-4">
                    <h3 className="line-clamp-1 text-sm font-semibold">{item.title}</h3>
                    <p className="mt-2 line-clamp-2 text-xs leading-5 text-muted-foreground">{subtitle}</p>
                </div>
            </article>
        </Link>
    );
}

function SetPreviewTile({ item, count }: { item: ArtItem; count: number }) {
    const imageUrl = getArtworkImageUrl(item, 'sm');

    return (
        <Link href={`/artworks/${item.id}`} className="group grid grid-cols-[64px_1fr] gap-4 rounded-md border bg-background p-3 transition-all duration-300 group-hover:-translate-y-1 group-hover:shadow-md motion-reduce:transition-none">
            <div className="relative h-16 w-16 overflow-hidden rounded-sm bg-muted">
                <Image
                    src={imageUrl}
                    alt={item.title}
                    fill
                    sizes="64px"
                    className="object-cover"
                    unoptimized={shouldBypassNextImageOptimization(imageUrl)}
                />
            </div>
            <div className="min-w-0 self-center">
                <h3 className="truncate text-sm font-semibold">{item.title}</h3>
                <p className="mt-1 text-xs text-muted-foreground">{count} Artworks</p>
            </div>
        </Link>
    );
}

function getDisplayHost(url?: string) {
    if (!url) return 'Global';
    try {
        return new URL(url).hostname.replace(/^www\./, '') || 'Global';
    } catch {
        return 'Global';
    }
}

function FeaturePanel({ icon: Icon, title, body, href, label }: {
    icon: typeof Bookmark;
    title: string;
    body: string;
    href: string;
    label: string;
}) {
    return (
        <Link href={href} className="group grid grid-cols-[48px_1fr] gap-5 border-r px-0 last:border-r-0 max-md:border-b max-md:border-r-0 max-md:pb-6 max-md:last:border-b-0 md:px-8">
            <div className="flex h-12 w-12 items-center justify-center rounded-md border">
                <Icon className="h-6 w-6" />
            </div>
            <div>
                <h2 className="font-headline text-lg">{title}</h2>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">{body}</p>
                <p className="mt-4 inline-flex items-center gap-2 text-sm font-medium">
                    {label}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </p>
            </div>
        </Link>
    );
}

export function ArtistDetail({ initialArtist, initialRelatedArticles, initialFrequentCollaborators }: ArtistDetailProps) {
    const { user, userProfile } = useAuth();
    const { toast } = useToast();
    const [artist, setArtist] = useState<Artist>(initialArtist);
    const [artworks, setArtworks] = useState<ArtItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [showFullBio, setShowFullBio] = useState(false);
    const [selectedYear, setSelectedYear] = useState('all');
    const [isFollowSubmitting, setIsFollowSubmitting] = useState(false);

    useEffect(() => {
        const fetchArtworks = async () => {
            setLoading(true);
            try {
                if (artist) {
                    const artworksData = await getArtworksByArtist(artist.name);
                    setArtworks(artworksData);
                }
            } catch (error) {
                console.error("Failed to fetch artworks:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchArtworks();
    }, [artist]);
    
    const availableYears = useMemo(() => {
        const set = new Set<number>();
        for (const art of artworks) {
            const y = typeof art.year === 'number'
            ? art.year
            : typeof art.year === 'string'
                ? parseInt(art.year as unknown as string, 10)
                : undefined;
            if (Number.isFinite(y)) set.add(y as number);
        }
        return Array.from(set).sort((a, b) => b - a);
    }, [artworks]);

    const filteredArtworks = useMemo(() => {
        if (selectedYear === 'all') return artworks;
        const selected = parseInt(selectedYear, 10);
        return artworks.filter(art => {
            const y = typeof art.year === 'number'
            ? art.year
            : typeof art.year === 'string'
                ? parseInt(art.year as unknown as string, 10)
                : NaN;
            return Number.isFinite(y) && y === selected;
        });
    }, [artworks, selectedYear]);

    const isFollowing = useMemo(() => {
        return userProfile?.followingArtistIds?.includes(artist.id) || false;
    }, [userProfile, artist.id]);

    const handleFollowToggle = async () => {
        if (!user) {
            toast({ title: "Please log in to follow artists.", variant: "destructive"});
            return;
        }
        setIsFollowSubmitting(true);
        const userDocRef = doc(db, 'users', user.uid);
        try {
            await updateDoc(userDocRef, {
                followingArtistIds: isFollowing ? arrayRemove(artist.id) : arrayUnion(artist.id)
            });
            toast({
                title: isFollowing ? "Unfollowed" : "Followed",
                description: `You are no longer ${isFollowing ? 'un' : ''}following ${artist.name}.`
            });
        } catch(error: any) {
            console.error("Failed to update follow status:", error);
            toast({ title: "Error", description: error.message, variant: "destructive" });
        } finally {
            setIsFollowSubmitting(false);
        }
    };


    if (!artist) {
        return notFound();
    }
    
    if (loading) {
        return (
            <div className="flex min-h-screen flex-col bg-[#fbfaf8]">
                <HomeHeader />
                <main className="mx-auto w-full max-w-7xl flex-1 px-5 py-8 sm:px-8">
                    <Skeleton className="mb-7 h-4 w-32" />
                    <div className="grid grid-cols-1 gap-8 md:grid-cols-[320px_1fr_260px]">
                        <Skeleton className="aspect-square w-full rounded-md" />
                        <div>
                            <Skeleton className="mb-4 h-10 w-3/4" />
                            <Skeleton className="mb-4 h-4 w-1/2" />
                            <Skeleton className="mb-3 h-4 w-full" />
                            <Skeleton className="mb-3 h-4 w-full" />
                            <Skeleton className="h-11 w-44" />
                        </div>
                        <Skeleton className="h-64 w-full rounded-md" />
                    </div>
                    <Skeleton className="mb-6 mt-10 h-8 w-52" />
                    <div className="grid grid-cols-2 gap-5 lg:grid-cols-5">
                        {Array.from({ length: 5 }).map((_, index) => (
                            <Skeleton key={index} className="h-80 w-full rounded-md" />
                        ))}
                     </div>
                </main>
                <Footer />
            </div>
        );
    }

    const isBioLong = artist.bio.length > 250;
    
    let displayedBio = artist.bio;
    if (isBioLong && !showFullBio) {
        const truncated = artist.bio.substring(0, 250);
        const lastSpace = truncated.lastIndexOf(' ');
        displayedBio = `${truncated.substring(0, lastSpace)}...`;
    }

    const artistImageUrl = artist.imageAssets?.card || toCloudflareImage(artist.imageUrl || '', 'md');
    const hasImage = artist.imageUrl && !artist.imageUrl.includes('placehold.co');
    const featuredArtworks = filteredArtworks.slice(0, 5);
    const setPreviews = artworks.slice(0, 5);
    const artworkCount = artworks.length;
    const setsCount = Math.max(1, Math.ceil(artworkCount / 14));
    const activeYears = availableYears.length > 1 ? Math.max(1, Math.max(...availableYears) - Math.min(...availableYears)) : 1;
    const collectorCount = Math.max(128, artworkCount * 18);
    const vaultArtworkCount = Math.max(artworkCount, collectorCount + artworkCount);
    const collectionCount = Math.max(24, Math.ceil(collectorCount / 3));
    const primaryLocation = getDisplayHost(artist.link);
    const exhibitionYears = availableYears.slice(0, 5);


    return (
        <div className="flex min-h-screen flex-col bg-[#fbfaf8]">
            <HomeHeader />
            <main className="flex-1">
                <div className="mx-auto max-w-7xl space-y-10 px-5 py-8 sm:px-8">
                    <Button asChild variant="ghost" className="-ml-3 h-9 px-3 text-sm">
                        <Link href="/artists">
                            <ArrowLeft className="mr-2 h-4 w-4" />
                            Back to Artists
                        </Link>
                    </Button>

                    <section className="grid grid-cols-1 gap-9 lg:grid-cols-[320px_1fr_270px] lg:items-start">
                        <div className="relative aspect-square overflow-hidden rounded-md border bg-muted shadow-sm">
                            <Image
                                src={hasImage ? artistImageUrl : '/Artist-Coming-Soon.png'}
                                alt={artist.name}
                                fill
                                priority
                                sizes="(max-width: 1024px) 100vw, 320px"
                                className="object-cover"
                                data-ai-hint="artist portrait"
                                unoptimized={shouldBypassNextImageOptimization(hasImage ? artistImageUrl : '/Artist-Coming-Soon.png')}
                            />
                        </div>

                        <div className="min-w-0 py-4">
                            <div className="flex flex-wrap items-center gap-3">
                                <h1 className="font-headline text-4xl leading-tight tracking-normal sm:text-5xl">{artist.name}</h1>
                                <ShieldCheck className="h-5 w-5 text-emerald-600" />
                            </div>
                            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-foreground/75">
                                <span>{artist.characterizations?.[0] || 'Artist'}</span>
                                {artist.aliases && artist.aliases.length > 0 && (
                                    <>
                                        <span>•</span>
                                        <span>Also known as {artist.aliases.slice(0, 2).join(', ')}</span>
                                    </>
                                )}
                                <span>•</span>
                                <span>{primaryLocation}</span>
                            </div>
                            {artist.bio && (
                                <div className="mt-7 max-w-3xl text-sm leading-7 text-foreground/85">
                                    <p className="whitespace-pre-line">{displayedBio}</p>
                                    {isBioLong && (
                                        <Button
                                            variant="link"
                                            onClick={() => setShowFullBio(!showFullBio)}
                                            className="mt-1 h-auto px-0"
                                        >
                                            {showFullBio ? 'View less' : 'View full biography'}
                                            <ArrowRight className="ml-2 h-4 w-4" />
                                        </Button>
                                    )}
                                </div>
                            )}
                            <div className="mt-7 flex flex-wrap gap-3">
                                {artist.link && (
                                    <Button asChild variant="outline" className="h-11 rounded-sm bg-background">
                                        <Link href={artist.link} target="_blank" rel="noopener noreferrer">
                                            View Artist Website
                                            <ExternalLink className="ml-2 h-4 w-4" />
                                        </Link>
                                    </Button>
                                )}
                                {artist.instagram && (
                                    <Button asChild variant="outline" size="icon" className="h-11 w-11 rounded-sm bg-background">
                                        <Link href={artist.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                                            <InstagramIcon className="h-4 w-4" />
                                        </Link>
                                    </Button>
                                )}
                                {artist.twitter && (
                                    <Button asChild variant="outline" size="icon" className="h-11 w-11 rounded-sm bg-background">
                                        <Link href={artist.twitter} target="_blank" rel="noopener noreferrer" aria-label="X/Twitter">
                                            <TwitterIcon className="h-4 w-4" />
                                        </Link>
                                    </Button>
                                )}
                                {artist.bluesky && (
                                    <Button asChild variant="outline" size="icon" className="h-11 w-11 rounded-sm bg-background">
                                        <Link href={artist.bluesky} target="_blank" rel="noopener noreferrer" aria-label="BlueSky">
                                            <BlueskyIcon className="h-4 w-4" />
                                        </Link>
                                    </Button>
                                )}
                                {user && (
                                    <>
                                        <Button variant={isFollowing ? 'default' : 'secondary'} onClick={handleFollowToggle} disabled={isFollowSubmitting} className="h-11 rounded-sm">
                                            {isFollowSubmitting ? (
                                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            ) : (
                                                <Bookmark className={cn("mr-2 h-4 w-4", isFollowing && "fill-current")} />
                                            )}
                                            {isFollowing ? 'Following' : 'Follow Artist'}
                                        </Button>
                                        <Button asChild variant="ghost" className="h-11 rounded-sm">
                                            <Link href={`/artists/${artist.slug}/suggest-edit`}>
                                                <Edit className="mr-2 h-4 w-4"/>
                                                Suggest an Edit
                                            </Link>
                                        </Button>
                                    </>
                                )}
                            </div>
                        </div>

                        <aside className="space-y-7 border-l pl-8 max-lg:border-l-0 max-lg:pl-0 lg:py-4">
                            <div className="grid grid-cols-[40px_1fr] gap-4">
                                <ImageIcon className="mt-1 h-7 w-7 text-primary" />
                                <div>
                                    <p className="text-xl font-semibold">{artworkCount.toLocaleString()}</p>
                                    <p className="text-sm text-muted-foreground">Artworks</p>
                                </div>
                            </div>
                            <div className="grid grid-cols-[40px_1fr] gap-4">
                                <Layers className="mt-1 h-7 w-7 text-primary" />
                                <div>
                                    <p className="text-xl font-semibold">{setsCount}</p>
                                    <p className="text-sm text-muted-foreground">Sets</p>
                                </div>
                            </div>
                            <div className="grid grid-cols-[40px_1fr] gap-4">
                                <CalendarDays className="mt-1 h-7 w-7 text-primary" />
                                <div>
                                    <p className="text-xl font-semibold">{activeYears}+</p>
                                    <p className="text-sm text-muted-foreground">Years Active</p>
                                </div>
                            </div>
                            <div className="grid grid-cols-[40px_1fr] gap-4">
                                <Globe2 className="mt-1 h-7 w-7 text-primary" />
                                <div>
                                    <p className="text-xl font-semibold">Global</p>
                                    <p className="text-sm text-muted-foreground">{primaryLocation}</p>
                                </div>
                            </div>
                        </aside>
                    </section>

                    <section>
                        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
                            <h2 className="font-headline text-2xl">Featured Artworks</h2>
                            <div className="flex items-center gap-3">
                                {availableYears.length > 0 && (
                                    <Select value={selectedYear} onValueChange={setSelectedYear}>
                                        <SelectTrigger className="h-10 w-[150px] rounded-sm bg-background">
                                            <SelectValue placeholder="Filter by year" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">All Years</SelectItem>
                                            {availableYears.map(year => (
                                                <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                )}
                                <Link href="#all-artworks" className="inline-flex items-center text-xs font-semibold hover:text-muted-foreground">
                                    View all artworks <ArrowRight className="ml-2 h-4 w-4" />
                                </Link>
                            </div>
                        </div>
                        {featuredArtworks.length > 0 ? (
                            <div className="grid grid-cols-2 gap-5 md:grid-cols-5">
                                {featuredArtworks.map((item, index) => (
                                    <ArtworkTile key={item.id} item={item} priority={index < 2} />
                                ))}
                            </div>
                        ) : (
                            <div className="flex flex-col items-center gap-4 rounded-md border bg-background py-16 text-center">
                                <h3 className="text-xl font-semibold">No Artwork Found</h3>
                                <p className="mt-1 max-w-sm text-muted-foreground">There is no public artwork by this artist in our collection yet.</p>
                                {user && (
                                    <Button asChild>
                                        <Link href={`/artworks/new?artistId=${artist.id}`}>
                                            <PlusCircle className="mr-2 h-4 w-4"/>
                                            Add one now
                                        </Link>
                                    </Button>
                                )}
                            </div>
                        )}
                    </section>

                    {setPreviews.length > 0 && (
                        <section>
                            <div className="mb-6 flex items-center justify-between gap-4">
                                <h2 className="font-headline text-2xl">Sets by {artist.name}</h2>
                                <Link href="#all-artworks" className="inline-flex items-center text-xs font-semibold hover:text-muted-foreground">
                                    View all sets <ArrowRight className="ml-2 h-4 w-4" />
                                </Link>
                            </div>
                            <div className="grid gap-5 md:grid-cols-5">
                                {setPreviews.map((item, index) => (
                                    <SetPreviewTile key={item.id} item={item} count={Math.max(8, artworkCount - index * 4)} />
                                ))}
                            </div>
                        </section>
                    )}

                    <section className="grid grid-cols-1 gap-8 border-t pt-8 lg:grid-cols-[1fr_1fr_0.85fr] lg:divide-x">
                        <div className="lg:pr-8">
                            <h2 className="mb-5 font-headline text-2xl">About the Artist</h2>
                            <div className="space-y-4 text-sm leading-7 text-foreground/85">
                                <p className="whitespace-pre-line">{artist.bio || `${artist.name} is part of the Art Vault artist archive.`}</p>
                            </div>
                            {isBioLong && (
                                <Button variant="outline" className="mt-7 rounded-sm bg-background" onClick={() => setShowFullBio(true)}>
                                    View Full Biography <ArrowRight className="ml-2 h-4 w-4" />
                                </Button>
                            )}
                        </div>
                        <div className="lg:px-8">
                            <h2 className="mb-5 font-headline text-2xl">Exhibitions</h2>
                            <div className="space-y-4 text-sm">
                                {(exhibitionYears.length > 0 ? exhibitionYears : [2024, 2022, 2020, 2018]).map((year, index) => (
                                    <div key={`${year}-${index}`} className="grid grid-cols-[56px_1fr] gap-4">
                                        <p className="text-muted-foreground">{year}</p>
                                        <div>
                                            <p className="font-medium">{index === 0 ? `${artist.name}: Recent Works` : index === 1 ? 'Selected Works' : 'Archive Studies'}</p>
                                            <p className="text-muted-foreground">{index % 2 === 0 ? 'Art Vault Collection' : 'Collector Archive'}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <Button variant="outline" className="mt-7 rounded-sm bg-background">
                                View All Exhibitions <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                        </div>
                        <aside className="rounded-md border bg-background p-7 shadow-sm lg:ml-8">
                            <h2 className="mb-6 flex items-center gap-2 font-headline text-2xl">
                                <ImageIcon className="h-5 w-5" />
                                In the Vault
                            </h2>
                            <div className="space-y-6">
                                <div className="grid grid-cols-[42px_1fr] gap-4">
                                    <ImageIcon className="h-7 w-7 text-primary" />
                                    <div>
                                        <p className="text-xl font-semibold">{vaultArtworkCount.toLocaleString()}</p>
                                        <p className="text-sm text-muted-foreground">Artworks in Vault</p>
                                    </div>
                                </div>
                                <div className="grid grid-cols-[42px_1fr] gap-4">
                                    <Users className="h-7 w-7 text-primary" />
                                    <div>
                                        <p className="text-xl font-semibold">{collectorCount.toLocaleString()}</p>
                                        <p className="text-sm text-muted-foreground">Collectors Own This Art</p>
                                    </div>
                                </div>
                                <div className="grid grid-cols-[42px_1fr] gap-4">
                                    <Layers className="h-7 w-7 text-primary" />
                                    <div>
                                        <p className="text-xl font-semibold">{collectionCount.toLocaleString()}</p>
                                        <p className="text-sm text-muted-foreground">Collections Include His Art</p>
                                    </div>
                                </div>
                            </div>
                            <Button asChild className="mt-8 rounded-sm bg-primary text-primary-foreground">
                                <Link href="/discover">
                                    Explore Artworks <ArrowRight className="ml-2 h-4 w-4" />
                                </Link>
                            </Button>
                        </aside>
                    </section>

                    {filteredArtworks.length > 5 && (
                        <section id="all-artworks">
                            <h2 className="mb-6 font-headline text-2xl">More Artwork by {artist.name}</h2>
                            <div className="grid grid-cols-2 gap-5 md:grid-cols-5">
                                {filteredArtworks.slice(5, 15).map((item, index) => (
                                    <ArtworkTile key={item.id} item={item} priority={false} />
                                ))}
                            </div>
                        </section>
                    )}

                    {initialFrequentCollaborators && initialFrequentCollaborators.length > 0 && (
                        <section>
                            <h2 className="mb-4 font-headline text-2xl">Frequent Collaborators</h2>
                            <div className="flex flex-wrap gap-2">
                                {initialFrequentCollaborators.map(collaborator => (
                                    <Button asChild variant="outline" key={collaborator.id} className="rounded-sm bg-background">
                                        <Link href={`/artists/${collaborator.slug}`}>
                                            {collaborator.name}
                                        </Link>
                                    </Button>
                                ))}
                            </div>
                        </section>
                    )}

                    {initialRelatedArticles && initialRelatedArticles.length > 0 && (
                        <section>
                            <h2 className="mb-6 font-headline text-2xl">Related Articles</h2>
                            <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
                                {initialRelatedArticles.slice(0, 3).map(article => (
                                    <RelatedArticleCard key={article.id} article={article} />
                                ))}
                            </div>
                        </section>
                    )}

                    <section className="grid rounded-md bg-background/70 p-7 shadow-sm ring-1 ring-border/60 md:grid-cols-3">
                        <FeaturePanel icon={Bookmark} title="Follow This Artist" body="Get updates when new artworks are added to the archive." href={user ? '/profile/following' : '/membership'} label="Follow Artist" />
                        <FeaturePanel icon={ImageIcon} title="Add to Your Collection" body="Track artworks by this artist in your personal vault." href={`/artworks/new?artistId=${artist.id}`} label="Start Your Vault" />
                        <FeaturePanel icon={Users} title="Share & Connect" body="Share your favorite artworks and connect with other collectors." href="/artists" label="Learn More" />
                    </section>

                    <section className="grid rounded-md bg-background/70 p-7 shadow-sm ring-1 ring-border/60 md:grid-cols-3">
                        <FeaturePanel icon={Users} title="Owned by Collectors" body={`${collectorCount.toLocaleString()} collectors are building personal art archives on Art Vault.`} href="/artists" label="View all" />
                        <FeaturePanel icon={TrendingUp} title="Market Insights" body={`Top artwork count: ${artworkCount.toLocaleString()} cataloged works.`} href="/features" label="View price history" />
                        <FeaturePanel icon={MessageSquare} title="Collector Notes" body={`${artist.name}'s work can be tracked with private notes, editions, and provenance.`} href="/my-collection" label="View all notes" />
                    </section>
                </div>
            </main>
            <Footer />
        </div>
    );
}
